import React, { FunctionComponent } from 'react';
import {
  Alert,
  Checkbox,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { privacyLabels, PrivacyLevel, TiktokMetadata } from '@monorepo/types';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

interface Props {
  metadata: TiktokMetadata;
  onUpdate: (metadata: Partial<TiktokMetadata>) => void;
}

const TikTokPostOptions: FunctionComponent<Props> = ({
  metadata,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const handlePrivacyChange = (value: PrivacyLevel) => {
    if (
      metadata.brandOptions.brandedContent &&
      value === PrivacyLevel.SELF_ONLY
    )
      return;
    onUpdate({ ...metadata, privacy: value });
  };

  const handleInteractionChange = (
    key: keyof Pick<
      TiktokMetadata['interactions'],
      'comment' | 'duet' | 'stitch'
    >,
    checked: boolean
  ) => {
    onUpdate({
      ...metadata,
      interactions: { ...metadata.interactions, [key]: checked },
    });
  };

  const privacyLevels =
    metadata.privacyLevels && metadata.privacyLevels.length > 0
      ? metadata.privacyLevels
      : Object.values(PrivacyLevel);

  const handleBrandOptionChange = (
    key: 'yourBrand' | 'brandedContent',
    checked: boolean
  ) => {
    if (
      key === 'brandedContent' &&
      checked &&
      metadata.privacy === PrivacyLevel.SELF_ONLY
    ) {
      const privacyLevel = privacyLevels.includes(
        PrivacyLevel.PUBLIC_TO_EVERYONE
      )
        ? PrivacyLevel.PUBLIC_TO_EVERYONE
        : PrivacyLevel.MUTUAL_FOLLOW_FRIENDS;

      onUpdate({
        ...metadata,
        privacy: privacyLevel,
        brandOptions: { ...metadata.brandOptions, [key]: checked },
      });
    } else {
      onUpdate({
        ...metadata,
        brandOptions: { ...metadata.brandOptions, [key]: checked },
      });
    }
  };

  const getAlertMessage = () => {
    if (
      metadata.brandOptions.brandedContent &&
      metadata.brandOptions.yourBrand
    ) {
      return t('videoEditor.socialPublish.tiktok.labelPaidPartnership');
    }
    if (metadata.brandOptions.brandedContent) {
      return t('videoEditor.socialPublish.tiktok.labelPaidPartnership');
    }
    if (metadata.brandOptions.yourBrand) {
      return t('videoEditor.socialPublish.tiktok.labelPromotional');
    }
    return t('videoEditor.socialPublish.tiktok.disclosureHint');
  };

  const interactionItems = [
    { key: 'comment', disabled: metadata.interactions.commentDisabled },
    { key: 'duet', disabled: metadata.interactions.duetDisabled },
    { key: 'stitch', disabled: metadata.interactions.stitchDisabled },
  ] as const;

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          {/*<Title level={5} style={{ margin: '0px' }}>Who can view this video</Title>*/}
          <Select
            placeholder={t('videoEditor.socialPublish.tiktok.whoCanView')}
            style={{ width: '100%' }}
            value={metadata.privacy}
            onChange={handlePrivacyChange}
          >
            {privacyLevels.map((privacyLevel) => (
              <Select.Option
                key={privacyLevel}
                value={privacyLevel}
                disabled={
                  privacyLevel === PrivacyLevel.SELF_ONLY &&
                  metadata.brandOptions.brandedContent
                }
              >
                <Tooltip
                  title={
                    privacyLevel === PrivacyLevel.SELF_ONLY &&
                    metadata.brandOptions.brandedContent
                      ? t(
                          'videoEditor.socialPublish.tiktok.brandedContentPrivateError'
                        )
                      : ''
                  }
                >
                  {privacyLabels[privacyLevel]}
                </Tooltip>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div>
          <Title level={5} style={{ margin: '0px' }}>
            {t('videoEditor.socialPublish.tiktok.allowUsersTo')}
          </Title>
          <Space>
            {interactionItems.map(({ key, disabled }) => (
              <Checkbox
                key={key}
                checked={metadata.interactions[key]}
                disabled={disabled}
                onChange={(e) => handleInteractionChange(key, e.target.checked)}
              >
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </Checkbox>
            ))}
          </Space>
        </div>

        <div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {t('videoEditor.socialPublish.tiktok.disclosureTitle')}
                </Title>
                <Text type="secondary">
                  {t('videoEditor.socialPublish.tiktok.disclosureDescription')}
                </Text>
              </div>
              <Switch
                checked={metadata.isDisclosureEnabled}
                onChange={(checked) =>
                  onUpdate({
                    isDisclosureEnabled: checked,
                    brandOptions: { yourBrand: false, brandedContent: false },
                  })
                }
              />
            </div>

            {metadata.isDisclosureEnabled && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message={getAlertMessage()}
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                />

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Tooltip
                    title={t(
                      'videoEditor.socialPublish.tiktok.yourBrandTooltip'
                    )}
                  >
                    <Checkbox
                      checked={metadata.brandOptions.yourBrand}
                      onChange={(e) =>
                        handleBrandOptionChange('yourBrand', e.target.checked)
                      }
                    >
                      <div>
                        <Text strong>
                          {t('videoEditor.socialPublish.tiktok.yourBrand')}
                        </Text>
                        <br />
                        <Text type="secondary">
                          {t(
                            'videoEditor.socialPublish.tiktok.yourBrandDescription'
                          )}
                        </Text>
                      </div>
                    </Checkbox>
                  </Tooltip>

                  <Tooltip
                    title={t(
                      'videoEditor.socialPublish.tiktok.brandedContentTooltip'
                    )}
                  >
                    <Checkbox
                      checked={metadata.brandOptions.brandedContent}
                      onChange={(e) =>
                        handleBrandOptionChange(
                          'brandedContent',
                          e.target.checked
                        )
                      }
                    >
                      <div>
                        <Text strong>
                          {t('videoEditor.socialPublish.tiktok.brandedContent')}
                        </Text>
                        <br />
                        <Text type="secondary">
                          {t(
                            'videoEditor.socialPublish.tiktok.brandedContentDescription'
                          )}
                        </Text>
                      </div>
                    </Checkbox>
                  </Tooltip>
                </Space>
              </Space>
            )}
          </Space>
        </div>
      </Space>
    </div>
  );
};

export default TikTokPostOptions;
